import { useEffect, useState, useCallback } from 'react'
import { apiGet, apiPost } from '../api'

interface CalendarEntry {
  id: string
  summary: string
  primary?: boolean
  accessRole?: string
  backgroundColor?: string
  provider?: string                // 'google' | 'ms:<label>'
  enabled: boolean
}

export default function CalendarPicker() {
  const [cals, setCals] = useState<CalendarEntry[] | null>(null)
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setErr(null)
      const r = await apiGet<{ calendars: CalendarEntry[] }>('/calendar/list')
      setCals(r.calendars)
    } catch (e) { setErr(String(e)) }
  }, [])

  useEffect(() => { if (open && !cals) load() }, [open, cals, load])

  async function toggle(id: string) {
    if (!cals || busy) return
    const next = cals.map(c => c.id === id ? { ...c, enabled: !c.enabled } : c)
    setCals(next)
    const ids = next.filter(c => c.enabled).map(c => c.primary ? 'primary' : c.id)
    if (ids.length === 0) return   // never let the user disable everything
    setBusy(true)
    try {
      await apiPost('/calendar/select', { ids })
    } catch (e) { setErr(String(e)); load() }
    finally { setBusy(false) }
  }

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)}
        style={{ minHeight: 26, padding: '2px 10px', fontSize: '0.68rem' }}>
        + calendars
      </button>
    )
  }

  return (
    <div style={{
      position: 'absolute', right: 0, top: '100%', marginTop: 6, zIndex: 20,
      background: 'var(--bg-elevated)', border: '1px solid var(--border-strong)',
      borderRadius: 'var(--radius-md)', padding: '0.6rem 0.7rem',
      boxShadow: '0 8px 28px rgba(0,0,0,0.5)', minWidth: 260, maxHeight: 320, overflowY: 'auto',
      backdropFilter: 'blur(14px)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span className="mono" style={{ fontSize: '0.65rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>
          Google calendars
        </span>
        <button type="button" onClick={() => setOpen(false)}
          style={{ minHeight: 22, padding: '0 8px', fontSize: '0.7rem' }}>×</button>
      </div>
      {err && <div style={{ fontSize: '0.7rem', color: 'var(--critical)' }}>{err}</div>}
      {!cals && !err && <div className="muted" style={{ fontSize: '0.75rem' }}>Loading…</div>}
      {cals && cals.length === 0 && (
        <div className="muted" style={{ fontSize: '0.75rem' }}>
          No calendars found. Connect Google first.
        </div>
      )}
      {cals && cals.map(c => {
        const providerTag = c.provider === 'google' ? 'G' : c.provider?.startsWith('ms:') ? '⊞' : '?'
        return (
          <label key={c.id} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '4px 0', fontSize: '0.78rem', cursor: 'pointer',
          }}>
            <input type="checkbox" checked={c.enabled} onChange={() => toggle(c.id)} />
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 16, height: 16, borderRadius: 3, flexShrink: 0,
              fontSize: '0.6rem', fontFamily: 'var(--font-mono)',
              color: c.provider === 'google' ? '#3ddc84' : 'var(--accent)',
              background: c.provider === 'google' ? 'rgba(61,220,132,0.12)' : 'rgba(90,242,255,0.12)',
              border: c.provider === 'google' ? '1px solid rgba(61,220,132,0.3)' : '1px solid rgba(90,242,255,0.3)',
            }}>{providerTag}</span>
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {c.summary}{c.primary ? ' (primary)' : ''}
            </span>
          </label>
        )
      })}
    </div>
  )
}
